import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, Button, Form, Input, Select, message, Space, Popconfirm, Card,
    Row, Col, Tag, Modal, Switch, Breadcrumb
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hospitalAdminService } from '../../services';

const ROLE_COLORS = { doctor: 'blue', secretary: 'cyan', lab: 'geekblue', pharmacy: 'purple', optician: 'gold' };

// What kind of doctor: [value, translation key]. A dentist and an eye doctor
// get their own sections on the visit form.
const DOCTOR_TYPES = [
    ['general', 'generalDoctor'],
    ['dentist', 'dentist'],
    ['eye', 'eyeDoctor']
];

const BranchStaff = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [branch, setBranch] = useState(null);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();
    const selectedRole = Form.useWatch('role', form);

    const fetchBranch = useCallback(async () => {
        setLoading(true);
        try {
            const res = await hospitalAdminService.getBranch(id);
            setBranch(res.data.branch);
            const grouped = res.data.staff || {};
            // Flatten grouped staff into a single list for the table
            const flat = Object.keys(grouped).reduce((acc, role) => acc.concat(grouped[role] || []), []);
            setStaff(flat);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => {
        fetchBranch();
    }, [fetchBranch]);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ role: 'doctor', doctorType: 'general', language: 'en' });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditing(record);
        form.setFieldsValue({
            role: record.role,
            name: record.name,
            email: record.email,
            address: record.address,
            specialization: record.specialization,
            doctorType: record.doctorType || 'general',
            language: record.language || 'en',
            isActive: record.isActive
        });
        setModalOpen(true);
    };

    const handleDelete = async (staffId) => {
        try {
            await hospitalAdminService.deleteStaff(staffId);
            message.success(t('success'));
            fetchBranch();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editing) {
                await hospitalAdminService.updateStaff(editing._id, values);
            } else {
                await hospitalAdminService.createStaff(id, values);
            }
            message.success(t('success'));
            setModalOpen(false);
            fetchBranch();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const columns = [
        { title: t('name'), dataIndex: 'name', key: 'name' },
        {
            title: t('role') || 'Role',
            dataIndex: 'role',
            key: 'role',
            render: (role, record) => {
                const type = DOCTOR_TYPES.find(([value]) => value !== 'general' && value === record.doctorType);
                return <Tag color={ROLE_COLORS[role]}>{type ? t(type[1]) : (t(role) || role)}</Tag>;
            }
        },
        { title: t('phoneNumber'), dataIndex: 'phoneNumber', key: 'phoneNumber' },
        { title: t('specialization') || 'Specialization', dataIndex: 'specialization', key: 'specialization', render: (v) => v || '-' },
        {
            title: t('status') || 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => isActive ? <Tag color="green">{t('active') || 'Active'}</Tag> : <Tag>{t('inactive') || 'Inactive'}</Tag>
        },
        {
            title: t('actions') || 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    <Popconfirm title={t('confirmDelete') || 'Delete?'} onConfirm={() => handleDelete(record._id)}>
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="page-container">
            <Breadcrumb style={{ marginBottom: 12 }} items={[
                { title: <a onClick={() => navigate('/hospitaladmin/branches')}>{t('branches') || 'Branches'}</a> },
                { title: branch?.name || '...' }
            ]} />

            <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                <Col>
                    <Space>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hospitaladmin/branches')} />
                        <h1 style={{ margin: 0 }}>{branch?.name} — {t('staff') || 'Staff'}</h1>
                    </Space>
                </Col>
                <Col>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        {t('addStaff') || 'Add Staff'}
                    </Button>
                </Col>
            </Row>

            <Card>
                <Table
                    columns={columns}
                    dataSource={staff}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 800 }}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Modal
                title={editing ? (t('editStaff') || 'Edit Staff') : (t('addStaff') || 'Add Staff')}
                open={modalOpen}
                onOk={handleSubmit}
                onCancel={() => setModalOpen(false)}
                okText={t('save') || 'Save'}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="role" label={t('role') || 'Role'} rules={[{ required: true }]}>
                        <Select
                            disabled={!!editing}
                            options={[
                                { value: 'doctor', label: t('doctor') || 'Doctor' },
                                { value: 'secretary', label: t('secretary') || 'Secretary' },
                                { value: 'lab', label: t('lab') || 'Lab' },
                                { value: 'pharmacy', label: t('pharmacy') || 'Pharmacy' },
                                { value: 'optician', label: t('optician') }
                            ]}
                        />
                    </Form.Item>
                    {selectedRole === 'doctor' && (
                        <Form.Item name="doctorType" label={t('doctorType')} extra={t('doctorTypeHint')}>
                            <Select options={DOCTOR_TYPES.map(([value, key]) => ({ value, label: t(key) }))} />
                        </Form.Item>
                    )}
                    <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    {selectedRole === 'doctor' && (
                        <Form.Item name="specialization" label={t('specialization') || 'Specialization'}>
                            <Input />
                        </Form.Item>
                    )}
                    {!editing && (
                        <>
                            <Form.Item name="phoneNumber" label={t('phoneNumber')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="password" label={t('password')} rules={[{ required: true }]}>
                                <Input.Password />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item name="email" label={t('email') || 'Email'}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="address" label={t('address') || 'Address'}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="language" label={t('language') || 'Language'}>
                        <Select
                            options={[
                                { value: 'en', label: 'English' },
                                { value: 'ar', label: 'العربية' },
                                { value: 'ku', label: 'کوردی' }
                            ]}
                        />
                    </Form.Item>
                    {editing && (
                        <Form.Item name="isActive" label={t('active') || 'Active'} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default BranchStaff;
